import { Component, HostBinding, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, ResolveEnd, Router } from '@angular/router';
import { AB_TEST_USER_JOURNEY_TRACKER, UserJourneyTrackerServiceInterface } from '@campus/ab-test';
import { combineLatest, filter, map, Observable, Subject } from 'rxjs';
import { mergeWith, takeUntil } from 'rxjs/operators';
import { WrapperViewModel } from './wrapper.viewmodel';

import '@diekeure/dcr-components/dcr-animated-kai.js';

@Component({
  standalone: false,
  selector: 'campus-root',
  template: `<router-outlet></router-outlet>
    <div
      data-cy="kai-loading"
      class="fixed flex items-center justify-center inset-0 transition-opacity index-overlay backdrop-filter backdrop-blur-xs bg-surface bg-opacity-low"
      [ngClass]="(isLoading$ | async) ? ['opacity-full'] : ['opacity-none', 'pointer-event-none']"
    >
      <div class="text-center">
        <dcr-animated-kai
          [src]="'kai_state_machine.riv' | assetsPath: 'rive'"
          autoplay
          state="LOADING"
        ></dcr-animated-kai>
        <div class="label-large">Bezig met laden...</div>
      </div>
    </div>
    <campus-error-dialog></campus-error-dialog>
    <campus-news-modal></campus-news-modal>
    <campus-learnosity-overlay></campus-learnosity-overlay>`,
  styles: [':host { --dcr-comp-animated-kai-size: 150px; }'],
})
export class AppWrapperComponent implements OnInit, OnDestroy {
  @HostBinding('class')
  defaultClasses = ['block', 'h-screen'];

  public isLoading$: Observable<boolean>;

  private destroy$ = new Subject<void>();

  private router: Router = inject(Router);
  private viewModel: WrapperViewModel = inject(WrapperViewModel);
  private userJourneyTracker: UserJourneyTrackerServiceInterface = inject(AB_TEST_USER_JOURNEY_TRACKER);

  public ngOnInit(): void {
    this.initializeStreams();
    this.startUserJourneyTracking();
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeStreams(): void {
    const resolveStart$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationStart),
      map(() => true)
    ); // Create an observable that emits false when a resolver ends or navigation completes/cancels/errors
    const resolveEndOrNavigationEnd$ = this.router.events.pipe(
      filter(
        (event) =>
          event instanceof ResolveEnd ||
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
      ),
      map(() => false)
    );

    // Merge the two observables.
    // When ResolveStart emits, isLoading$ becomes true.
    // When any of the "end" events emit, isLoading$ becomes false.
    this.isLoading$ = resolveStart$.pipe(mergeWith(resolveEndOrNavigationEnd$));
  }

  private startUserJourneyTracking() {
    combineLatest([this.viewModel.currentUser$, this.viewModel.userJourneyContext$])
      .pipe(
        takeUntil(this.destroy$),
        filter(([user]) => !!user?.id)
      )
      .subscribe(([user, userJourneyContext]) =>
        this.userJourneyTracker.startJourney(user.id.toString(), userJourneyContext)
      );
  }
}
